import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ChevronLeft, RotateCcw, Save, Eye, EyeOff, Palette, Type, Layout, Code2, Image as ImageIcon } from 'lucide-react';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import LivePreviewPanel from './LivePreviewPanel';

/**
 * Per-tenant Theme Editor — master/tenant admin assigns colours, brand
 * name, logos and custom CSS for a specific tenant. Changes are persisted
 * via PUT /api/admin/tenants/{id}/theme. A live-preview toggle pushes the
 * current draft onto :root so the admin can see exactly what every player
 * surface will look like.
 */

// ---- Field groupings — mirrors the backend DEFAULT_THEME shape so any
//      key not listed here still round-trips, just edited in the JSON tab.
const GROUPS = [
  {
    id: 'brand',
    title: 'Brand identity',
    icon: ImageIcon,
    fields: [
      { key: 'brand_name',     label: 'Brand name',          type: 'text' },
      { key: 'tagline',        label: 'Tagline',             type: 'text' },
      { key: 'logo_url',       label: 'Header logo URL',     type: 'text' },
      { key: 'intro_logo_url', label: 'Intro splash logo',   type: 'text' },
    ],
  },
  {
    id: 'global',
    title: 'Global palette',
    icon: Palette,
    fields: [
      { key: 'neon',          label: 'Accent (neon)',       type: 'color' },
      { key: 'bg',            label: 'Background',          type: 'color' },
      { key: 'surface',       label: 'Surface',             type: 'color' },
      { key: 'surface_alt',   label: 'Surface (alt)',       type: 'color' },
      { key: 'text_primary',  label: 'Text primary',        type: 'color' },
      { key: 'text_muted',    label: 'Text muted',          type: 'text'  },
      { key: 'border',        label: 'Border',              type: 'text'  },
      { key: 'glow',          label: 'Glow',                type: 'text'  },
    ],
  },
  {
    id: 'btn-primary',
    title: 'Primary button (Play Now)',
    icon: Layout,
    fields: [
      { key: 'btn_primary_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_primary_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_primary_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'btn-add-credits',
    title: 'Add Credits button',
    icon: Layout,
    fields: [
      { key: 'btn_add_credits_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_add_credits_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_add_credits_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'btn-withdraw',
    title: 'Withdraw button',
    icon: Layout,
    fields: [
      { key: 'btn_withdraw_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_withdraw_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_withdraw_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'btn-warning',
    title: 'Warning button (Cashout Rules / Add Games)',
    icon: Layout,
    fields: [
      { key: 'btn_warning_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_warning_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_warning_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'btn-dark',
    title: 'Dark button (Refresh / Reset)',
    icon: Layout,
    fields: [
      { key: 'btn_dark_bg',   label: 'Background',  type: 'color' },
      { key: 'btn_dark_text', label: 'Text color',  type: 'color' },
    ],
  },
  {
    id: 'header',
    title: 'Header',
    icon: Layout,
    fields: [
      { key: 'header_from',   label: 'Gradient from', type: 'color' },
      { key: 'header_to',     label: 'Gradient to',   type: 'color' },
      { key: 'header_text',   label: 'Text',          type: 'color' },
      { key: 'header_accent', label: 'Accent',        type: 'color' },
    ],
  },
  {
    id: 'page-accents',
    title: 'Page accents',
    icon: Palette,
    fields: [
      { key: 'page_roulette_accent',    label: 'Roulette',    type: 'color' },
      { key: 'page_marketplace_accent', label: 'Marketplace', type: 'color' },
      { key: 'page_tasks_accent',       label: 'Tasks',       type: 'color' },
      { key: 'page_tiers_accent',       label: 'Tiers',       type: 'color' },
      { key: 'page_referral_accent',    label: 'Referral',    type: 'color' },
    ],
  },
  {
    id: 'landing',
    title: 'Landing page',
    icon: Palette,
    fields: [
      { key: 'landing_bg',                  label: 'Page background (CSS)',     type: 'text' },
      { key: 'landing_play_from',           label: 'Play Now — from',           type: 'color' },
      { key: 'landing_play_to',             label: 'Play Now — to',             type: 'color' },
      { key: 'landing_play_text',           label: 'Play Now — text',           type: 'color' },
      { key: 'landing_banner1_accent',      label: 'Banner 1 — accent',         type: 'color' },
      { key: 'landing_banner1_accent_deep', label: 'Banner 1 — accent deep',    type: 'color' },
      { key: 'landing_banner1_bg',          label: 'Banner 1 — bg gradient',    type: 'text'  },
      { key: 'landing_banner2_accent',      label: 'Banner 2 — accent',         type: 'color' },
      { key: 'landing_banner2_accent_deep', label: 'Banner 2 — accent deep',    type: 'color' },
      { key: 'landing_banner2_bg',          label: 'Banner 2 — bg gradient',    type: 'text'  },
      { key: 'landing_banner3_accent',      label: 'Banner 3 — accent',         type: 'color' },
      { key: 'landing_banner3_accent_deep', label: 'Banner 3 — accent deep',    type: 'color' },
      { key: 'landing_banner3_bg',          label: 'Banner 3 — bg gradient',    type: 'text'  },
    ],
  },
  {
    id: 'profile',
    title: 'Profile page',
    icon: Layout,
    fields: [
      { key: 'profile_accent',        label: 'Profile accent (icons, dots)',  type: 'color' },
      { key: 'profile_avatar_ring',   label: 'Avatar gold ring',              type: 'color' },
      { key: 'profile_card_bg',       label: 'Settings card background',      type: 'text'  },
      { key: 'profile_card_border',   label: 'Settings card border',          type: 'text'  },
      { key: 'profile_icon_bg',       label: 'Settings icon gradient (CSS)',  type: 'text'  },
    ],
  },
  {
    id: 'btn-save',
    title: 'Save / Submit button',
    icon: Layout,
    fields: [
      { key: 'btn_save_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_save_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_save_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'btn-logout',
    title: 'Logout / Danger button',
    icon: Layout,
    fields: [
      { key: 'btn_logout_from', label: 'Gradient from', type: 'color' },
      { key: 'btn_logout_to',   label: 'Gradient to',   type: 'color' },
      { key: 'btn_logout_text', label: 'Text color',    type: 'color' },
    ],
  },
  {
    id: 'typography',
    title: 'Typography',
    icon: Type,
    fields: [
      { key: 'font_family', label: 'Font family stack', type: 'text' },
    ],
  },
];

// Color picker swatch + hex input. Falls back to plain text input when
// the value is not a hex (e.g. rgba(...)).
function ColorField({ value, onChange, defaultValue }) {
  const isHex = typeof value === 'string' && /^#[0-9a-f]{3,8}$/i.test(value);
  const colorVal = isHex ? value : '#000000';
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={colorVal}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10"
        aria-label="color picker"
      />
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={defaultValue || ''}
        className="flex-1 bg-[#0B0E14] border-white/10 text-white text-xs font-mono"
      />
    </div>
  );
}

function TextField({ value, onChange, defaultValue }) {
  return (
    <Input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={defaultValue || ''}
      className="bg-[#0B0E14] border-white/10 text-white text-xs"
    />
  );
}

export default function TenantThemeEditorPage() {
  const { tenantSlug: adminBaseSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { previewTheme, refresh: refreshActiveTheme, tenantSlug: activeSlug } = useTenantTheme();

  // URL pattern: /admin/tenant-theme/<tenantId>  OR  /<slug>/admin/tenant-theme/<tenantId>
  const tenantId = useMemo(() => {
    const m = location.pathname.match(/\/admin\/tenant-theme\/([\w-]+)/i);
    return m ? m[1] : null;
  }, [location.pathname]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [draft, setDraft] = useState({});
  const [livePreview, setLivePreview] = useState(false);
  const [tab, setTab] = useState('visual');     // 'visual' | 'css' | 'json'

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [themeRes, listRes] = await Promise.all([
          api.get(`/api/admin/tenants/${tenantId}/theme`),
          api.get('/api/admin/tenants'),
        ]);
        if (cancel) return;
        setDefaults(themeRes.data?.default_theme || {});
        setDraft(themeRes.data?.raw_theme || {});
        const list = listRes.data?.items || [];
        setTenant(list.find(t => t.id === tenantId) || null);
      } catch (e) {
        toast.error('Failed to load theme: ' + (e?.response?.data?.detail || e.message));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [tenantId]);

  // Whenever draft + livePreview is on, push it onto :root so the admin
  // sees the change without leaving the page.
  useEffect(() => {
    if (livePreview) previewTheme({ ...defaults, ...draft });
    return () => { if (livePreview) previewTheme(null); };
  }, [draft, livePreview, defaults, previewTheme]);

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));
  const resetField = (key) => setDraft(d => { const n = { ...d }; delete n[key]; return n; });

  const merged = useMemo(() => ({ ...defaults, ...draft }), [defaults, draft]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/tenants/${tenantId}/theme`, { theme: draft });
      toast.success('Theme saved');
      // If the admin is currently previewing the same tenant they're editing,
      // refresh the global ThemeProvider so any open player tab inherits.
      if (tenant?.slug === activeSlug) refreshActiveTheme();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset this tenant\'s entire theme to defaults? This will also clear custom CSS.')) return;
    try {
      await api.delete(`/api/admin/tenants/${tenantId}/theme`);
      setDraft({});
      toast.success('Theme reset to defaults');
      if (tenant?.slug === activeSlug) refreshActiveTheme();
    } catch (e) {
      toast.error('Reset failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const backHref = adminBaseSlug ? `/${adminBaseSlug}/admin/tenants` : '/admin/tenants';

  if (loading) {
    return (
      <div className="p-8 text-white/70">Loading theme…</div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 text-white" data-testid="tenant-theme-editor-page">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backHref)}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition"
            data-testid="back-to-tenants-btn"
            aria-label="Back to tenants"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold">{tenant?.name || 'Tenant'} · Theme</h1>
            <p className="text-xs text-white/50 font-mono">{tenant?.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLivePreview(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition ${livePreview ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'bg-white/5 border-white/10 text-white/70'}`}
            data-testid="toggle-live-preview-btn"
          >
            {livePreview ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Live preview
          </button>
          <Button
            variant="outline"
            onClick={resetAll}
            className="border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs"
            data-testid="reset-theme-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Reset all
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#22FF88] text-black font-bold text-xs"
            data-testid="save-theme-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            {saving ? 'Saving…' : 'Save theme'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-white/10 pb-2">
        {[
          { id: 'visual', label: 'Visual', icon: Palette },
          { id: 'css',    label: 'Custom CSS', icon: Code2 },
          { id: 'json',   label: 'Raw JSON',  icon: Code2 },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${tab === t.id ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
            data-testid={`theme-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Content + preview — preview pane gets more room (420px) since it
          now renders a full-fidelity mobile-shaped player home. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-5">
        <div className="min-w-0">
          {tab === 'visual' && (
            <div className="space-y-4">
              {GROUPS.map(group => (
                <details
                  key={group.id}
                  open
                  className="rounded-xl border border-white/10 bg-[#0B0E14]"
                  data-testid={`theme-group-${group.id}`}
                >
                  <summary className="px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-white/5 rounded-t-xl">
                    <group.icon className="w-4 h-4 text-emerald-400" />
                    <span className="font-bold text-sm">{group.title}</span>
                  </summary>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 border-t border-white/5">
                    {group.fields.map(f => (
                      <div key={f.key}>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-[11px] uppercase tracking-wider text-white/60">{f.label}</label>
                          {(draft[f.key] !== undefined && draft[f.key] !== '') && (
                            <button
                              onClick={() => resetField(f.key)}
                              className="text-[10px] text-white/40 hover:text-white"
                              data-testid={`reset-${f.key}-btn`}
                            >
                              reset
                            </button>
                          )}
                        </div>
                        {f.type === 'color' ? (
                          <ColorField
                            value={draft[f.key] !== undefined ? draft[f.key] : (defaults[f.key] || '')}
                            onChange={(v) => setField(f.key, v)}
                            defaultValue={defaults[f.key]}
                          />
                        ) : (
                          <TextField
                            value={draft[f.key] !== undefined ? draft[f.key] : (defaults[f.key] || '')}
                            onChange={(v) => setField(f.key, v)}
                            defaultValue={defaults[f.key]}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}

          {tab === 'css' && (
            <div>
              <p className="text-xs text-white/60 mb-2">
                Injected verbatim into the page &lt;head&gt;. Scope your selectors so they don't leak (e.g. <code className="text-emerald-300">.player-app .my-class</code>).
              </p>
              <textarea
                value={draft.custom_css || ''}
                onChange={(e) => setField('custom_css', e.target.value)}
                rows={20}
                placeholder="/* Custom CSS for this tenant */
.player-app .display { letter-spacing: 0.02em; }"
                className="w-full bg-[#05080F] border border-white/10 rounded-xl p-3 text-xs font-mono text-emerald-200"
                data-testid="custom-css-textarea"
              />
            </div>
          )}

          {tab === 'json' && (
            <div>
              <p className="text-xs text-white/60 mb-2">
                Raw theme overrides currently saved (only fields the admin has changed). Edit and save to apply.
              </p>
              <textarea
                value={JSON.stringify(draft, null, 2)}
                onChange={(e) => {
                  try { setDraft(JSON.parse(e.target.value)); }
                  catch { /* ignore until valid */ }
                }}
                rows={24}
                className="w-full bg-[#05080F] border border-white/10 rounded-xl p-3 text-xs font-mono text-amber-200"
                data-testid="theme-json-textarea"
              />
            </div>
          )}
        </div>

        {/* PREVIEW PANE — full-fidelity live mock for every major page.
            Tabs let admins switch between Home / Roulette / Marketplace /
            Tasks / Tiers / Referral / Wallet / Profile. The same theme
            tokens drive every page so every surface re-paints on edit. */}
        <aside className="lg:sticky lg:top-4 self-start space-y-3">
          <LivePreviewPanel theme={merged} />
          <div className="text-[11px] text-white/50 leading-relaxed">
            <strong className="text-white/80">Live preview</strong> mirrors every major player page — switch tabs above to verify the theme on Roulette, Marketplace, Tasks, Tiers, Referral, Wallet and Profile. Toggle <em>Live preview</em> in the top-bar to also push the draft onto the editor's chrome. Save to persist.
          </div>
        </aside>
      </div>
    </div>
  );
}
